import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LightColors, DarkColors, ColorScheme } from '../theme/colors';
import { FontFamily, FontSize, FontWeight, LineHeight } from '../theme/typography';
import { Spacing } from '../theme/spacing';
import { Radii } from '../theme/radii';
import { Shadows } from '../theme/shadows';
import { Motion } from '../theme/motion';
import { Platform } from 'react-native';

interface Theme {
  colors: ColorScheme;
  font: typeof FontFamily;
  fontSize: typeof FontSize;
  fontWeight: typeof FontWeight;
  lineHeight: typeof LineHeight;
  spacing: typeof Spacing;
  radii: typeof Radii;
  shadows: typeof Shadows;
  motion: typeof Motion;
  isDark: boolean;
}

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem('theme').then(val => {
      if (val === 'dark') setIsDark(true);
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDark(d => {
      const next = !d;
      AsyncStorage.setItem('theme', next ? 'dark' : 'light');
      if (Platform.OS === 'web') {
        document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
      }
      return next;
    });
  }, []);

  const theme: Theme = {
    colors: isDark ? DarkColors : LightColors,
    font: FontFamily,
    fontSize: FontSize,
    fontWeight: FontWeight,
    lineHeight: LineHeight,
    spacing: Spacing,
    radii: Radii,
    shadows: Shadows,
    motion: Motion,
    isDark,
  };

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Forces the light ("white") theme for everything inside it, regardless of the
// app's dark-mode setting. The auth flow (login/signup/etc.) is wrapped in this
// so those screens — and every input/button beneath them, which all read the
// nearest ThemeContext — stay on a stable white surface even in dark mode.
const forcedLightTheme: Theme = {
  colors: LightColors,
  font: FontFamily,
  fontSize: FontSize,
  fontWeight: FontWeight,
  lineHeight: LineHeight,
  spacing: Spacing,
  radii: Radii,
  shadows: Shadows,
  motion: Motion,
  isDark: false,
};

export function ForcedLightThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <ThemeContext.Provider value={{ theme: forcedLightTheme, isDark: false, toggleTheme: () => {} }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

// Non-throwing accessor for components that may render outside the provider
// (e.g. the top-level error boundary which wraps ThemeProvider). Falls back to
// the default light theme when no provider is present.
const fallbackTheme: Theme = {
  colors: LightColors,
  font: FontFamily,
  fontSize: FontSize,
  fontWeight: FontWeight,
  lineHeight: LineHeight,
  spacing: Spacing,
  radii: Radii,
  shadows: Shadows,
  motion: Motion,
  isDark: false,
};

export function useThemeSafe(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  return { theme: fallbackTheme, isDark: false, toggleTheme: () => {} };
}
